import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { blogApi } from "../../api/blogApi";
import { useAuth } from "../../context/AuthContext";

const CDN = "https://r2-proxy.seohoon0707.workers.dev";

const T = {
  bg: "var(--color-background)",
  bgSecond: "var(--color-background-secondary)",
  border: "var(--color-border-secondary)",
  text: "var(--color-text-primary)",
  textMuted: "var(--color-text-secondary)",
  accent: "#378ADD",
  danger: "#E24B4A",
  radius: "8px",
};

// ─────────────────────────────────────────────
// 마크다운 툴바 버튼 정의
// ─────────────────────────────────────────────
const TOOLBAR = [
  { label: "H1", wrap: ["# ", ""], block: true },
  { label: "H2", wrap: ["## ", ""], block: true },
  { label: "H3", wrap: ["### ", ""], block: true },
  { label: "B", wrap: ["**", "**"], block: false },
  { label: "I", wrap: ["*", "*"], block: false },
  { label: "~~", wrap: ["~~", "~~"], block: false },
  { label: "`", wrap: ["`", "`"], block: false },
  { label: "```", wrap: ["```\n", "\n```"], block: true },
  { label: "—", sep: true },
  { label: "링크", wrap: ["[", "](url)"], block: false },
  { label: "인용", wrap: ["\n> ", ""], block: true },
  { label: "목록", wrap: ["\n- ", ""], block: true },
  { label: "번호", wrap: ["\n1. ", ""], block: true },
  { label: "---", wrap: ["\n---\n", ""], block: true },
];

// ─────────────────────────────────────────────
// TagInput — IME(한국어) 조합 완료 후 태그 추가
// ─────────────────────────────────────────────
const TagInput = ({ tags, onChange }) => {
  const [input, setInput] = useState("");
  const isComposing = useRef(false); // IME 조합 중 여부

  const addTag = useCallback(
    (value) => {
      const trimmed = (value ?? input).trim().toLowerCase();
      if (trimmed && !tags.includes(trimmed) && tags.length < 10) {
        onChange([...tags, trimmed]);
      }
      setInput("");
    },
    [input, tags, onChange]
  );

  const removeTag = (tag) => onChange(tags.filter((t) => t !== tag));

  const handleKey = (e) => {
    // IME 조합 중이면 무시 — 한국어 마지막 글자 중복 방지
    if (isComposing.current) return;
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag();
    } else if (e.key === "Backspace" && !input && tags.length) {
      removeTag(tags[tags.length - 1]);
    }
  };

  return (
    <div style={{ marginBottom: "24px" }}>
      <label
        style={{
          fontSize: "13px",
          fontWeight: 600,
          color: T.textMuted,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          display: "block",
          marginBottom: "10px",
        }}
      >
        태그{" "}
        <span style={{ fontWeight: 400, textTransform: "none" }}>
          ({tags.length}/10)
        </span>
      </label>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "6px",
          alignItems: "center",
          padding: "8px 12px",
          border: `1px solid ${T.border}`,
          borderRadius: T.radius,
          background: T.bgSecond,
          minHeight: "46px",
        }}
      >
        {tags.map((tag) => (
          <span
            key={tag}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              background: `${T.accent}20`,
              color: T.accent,
              border: `1px solid ${T.accent}40`,
              borderRadius: "99px",
              padding: "2px 10px",
              fontSize: "13px",
              fontWeight: 500,
            }}
          >
            #{tag}
            <button
              type="button"
              onClick={() => removeTag(tag)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: T.accent,
                padding: "0 0 0 2px",
                fontSize: "14px",
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </span>
        ))}
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKey}
          onCompositionStart={() => {
            isComposing.current = true;
          }}
          onCompositionEnd={(e) => {
            isComposing.current = false;
            // 조합 완료된 글자가 Enter/쉼표면 태그로 등록
            if (e.data === "\n" || e.data === ",") addTag();
          }}
          // onBlur는 IME 조합 완료 후에만 태그 추가
          onBlur={() => {
            if (!isComposing.current) addTag();
          }}
          placeholder={
            tags.length === 0 ? "태그 입력 후 Enter (최대 10개)" : ""
          }
          maxLength={32}
          style={{
            flex: 1,
            minWidth: "120px",
            border: "none",
            outline: "none",
            background: "transparent",
            color: T.text,
            fontSize: "14px",
          }}
        />
      </div>
      <div style={{ fontSize: "11px", color: T.textMuted, marginTop: "4px" }}>
        Enter 또는 , 로 태그 추가
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// ImagePanel — 업로드 + 이미지 목록 + 본문 삽입
// ─────────────────────────────────────────────
const ImagePanel = ({ uploadedImages, onUpload, onDelete, onInsert }) => {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef();

  const handleFiles = useCallback(
    async (files) => {
      const imgs = [...files].filter((f) => f.type.startsWith("image/"));
      if (!imgs.length) return;
      setUploading(true);
      setProgress(0);
      try {
        for (const file of imgs) {
          if (file.size > 10 * 1024 * 1024) {
            alert(`${file.name}: 최대 10MB`);
            continue;
          }
          const { data } = await blogApi.uploadImage(file, setProgress);
          onUpload(data);
        }
      } catch {
        alert("이미지 업로드에 실패했습니다.");
      } finally {
        setUploading(false);
        setProgress(0);
      }
    },
    [onUpload]
  );

  return (
    <div style={{ marginBottom: "24px" }}>
      <label
        style={{
          fontSize: "13px",
          fontWeight: 600,
          color: T.textMuted,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          display: "block",
          marginBottom: "10px",
        }}
      >
        이미지
      </label>

      {/* 드롭존 */}
      <div
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          handleFiles(e.dataTransfer.files);
        }}
        style={{
          border: `2px dashed ${dragging ? T.accent : T.border}`,
          borderRadius: T.radius,
          padding: "20px 16px",
          textAlign: "center",
          cursor: "pointer",
          background: dragging ? `${T.accent}10` : T.bgSecond,
          transition: "all 0.15s",
        }}
      >
        {uploading ? (
          <div>
            <div
              style={{
                fontSize: "13px",
                color: T.textMuted,
                marginBottom: "8px",
              }}
            >
              업로드 중… {progress}%
            </div>
            <div
              style={{
                height: "4px",
                background: T.border,
                borderRadius: "2px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${progress}%`,
                  background: T.accent,
                  transition: "width 0.2s",
                }}
              />
            </div>
          </div>
        ) : (
          <div>
            <div style={{ fontSize: "24px", marginBottom: "4px" }}>🖼️</div>
            <div style={{ fontSize: "13px", color: T.textMuted }}>
              클릭 또는 드래그로 이미지 업로드
            </div>
            <div
              style={{ fontSize: "11px", color: T.textMuted, marginTop: "2px" }}
            >
              JPG · PNG · WebP · GIF · 최대 10MB
            </div>
          </div>
        )}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: "none" }}
        onChange={(e) => handleFiles(e.target.files)}
      />

      {/* 업로드된 이미지 목록 */}
      {uploadedImages.length > 0 && (
        <div style={{ marginTop: "12px" }}>
          <div
            style={{
              fontSize: "12px",
              color: T.textMuted,
              marginBottom: "8px",
            }}
          >
            업로드된 이미지 — <strong style={{ color: T.accent }}>삽입</strong>{" "}
            버튼으로 커서 위치에 추가
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
            {uploadedImages.map((img) => (
              <div
                key={img.key}
                style={{ position: "relative", width: "100px" }}
              >
                <img
                  src={img.thumbnailUrl}
                  alt=""
                  style={{
                    width: "100px",
                    height: "72px",
                    objectFit: "cover",
                    borderRadius: "6px",
                    border: `1px solid ${T.border}`,
                    display: "block",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: "6px",
                    background: "rgba(0,0,0,0.5)",
                    opacity: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "5px",
                    transition: "opacity 0.15s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = 0)}
                >
                  <button
                    type="button"
                    onClick={() => onInsert(img.url)}
                    style={{
                      background: T.accent,
                      border: "none",
                      borderRadius: "4px",
                      color: "#fff",
                      fontSize: "11px",
                      padding: "4px 7px",
                      cursor: "pointer",
                      fontWeight: 600,
                    }}
                  >
                    삽입
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(img.key)}
                    style={{
                      background: T.danger,
                      border: "none",
                      borderRadius: "4px",
                      color: "#fff",
                      fontSize: "11px",
                      padding: "4px 7px",
                      cursor: "pointer",
                    }}
                  >
                    삭제
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// ThumbnailPicker
// ─────────────────────────────────────────────
const ThumbnailPicker = ({ uploadedImages, thumbnailUrl, onSelect }) => {
  if (!uploadedImages.length) return null;
  return (
    <div style={{ marginBottom: "24px" }}>
      <label
        style={{
          fontSize: "13px",
          fontWeight: 600,
          color: T.textMuted,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          display: "block",
          marginBottom: "10px",
        }}
      >
        대표 이미지{" "}
        <span style={{ fontWeight: 400, textTransform: "none" }}>
          (클릭으로 선택/해제)
        </span>
      </label>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
        {uploadedImages.map((img) => (
          <div
            key={img.key}
            onClick={() =>
              onSelect(
                thumbnailUrl === img.thumbnailUrl ? null : img.thumbnailUrl
              )
            }
            style={{
              width: "100px",
              height: "72px",
              cursor: "pointer",
              borderRadius: "6px",
              border: `2px solid ${
                thumbnailUrl === img.thumbnailUrl ? T.accent : T.border
              }`,
              overflow: "hidden",
              transition: "border-color 0.15s",
              position: "relative",
            }}
          >
            <img
              src={img.thumbnailUrl}
              alt=""
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
            {thumbnailUrl === img.thumbnailUrl && (
              <div
                style={{
                  position: "absolute",
                  top: 3,
                  right: 3,
                  background: T.accent,
                  borderRadius: "99px",
                  width: "16px",
                  height: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "10px",
                  color: "#fff",
                }}
              >
                ✓
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// 마크다운 에디터 (툴바 + textarea)
// ─────────────────────────────────────────────
const MarkdownEditor = ({ value, onChange, textareaRef }) => {
  const applyFormat = (item) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const { selectionStart: s, selectionEnd: e } = ta;
    const selected = value.slice(s, e);
    const [before, after] = item.wrap;
    const inserted = before + (selected || "텍스트") + after;
    const next = value.slice(0, s) + inserted + value.slice(e);
    onChange(next);
    setTimeout(() => {
      const cursor =
        s + before.length + (selected || "텍스트").length + after.length;
      ta.selectionStart = ta.selectionEnd = cursor;
      ta.focus();
    }, 0);
  };

  return (
    <div style={{ marginBottom: "28px" }}>
      <label
        style={{
          fontSize: "13px",
          fontWeight: 600,
          color: T.textMuted,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          display: "block",
          marginBottom: "10px",
        }}
      >
        본문{" "}
        <span style={{ fontWeight: 400, textTransform: "none" }}>
          — 마크다운 지원 (#, **, * 등)
        </span>
      </label>

      {/* 툴바 */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "4px",
          padding: "8px 10px",
          background: T.bgSecond,
          border: `1px solid ${T.border}`,
          borderBottom: "none",
          borderRadius: `${T.radius} ${T.radius} 0 0`,
        }}
      >
        {TOOLBAR.map((item, i) =>
          item.sep ? (
            <div
              key={i}
              style={{
                width: "1px",
                background: T.border,
                margin: "2px 4px",
                alignSelf: "stretch",
              }}
            />
          ) : (
            <button
              key={i}
              type="button"
              onClick={() => applyFormat(item)}
              title={item.label}
              style={{
                padding: "4px 8px",
                border: `1px solid ${T.border}`,
                borderRadius: "4px",
                background: "transparent",
                color: T.text,
                fontSize: "12px",
                cursor: "pointer",
                fontWeight:
                  item.label === "B"
                    ? 700
                    : item.label === "I"
                    ? "italic"
                    : 400,
                fontFamily: ["```", "`"].includes(item.label)
                  ? "monospace"
                  : "inherit",
                transition: "background 0.1s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = `${T.accent}18`)
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "transparent")
              }
            >
              {item.label}
            </button>
          )
        )}
      </div>

      {/* textarea */}
      <textarea
        ref={textareaRef}
        placeholder={`# 제목\n\n내용을 입력하세요…\n\n## 소제목\n\n- 목록 항목\n- 항목 2\n\n**굵게**, *기울임*, \`코드\``}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={24}
        style={{
          width: "100%",
          padding: "14px 16px",
          border: `1px solid ${T.border}`,
          borderRadius: `0 0 ${T.radius} ${T.radius}`,
          fontSize: "14px",
          background: T.bgSecond,
          color: T.text,
          boxSizing: "border-box",
          outline: "none",
          resize: "vertical",
          lineHeight: "1.8",
          fontFamily: "'Nanum Gothic Coding', 'D2Coding', monospace",
          tabSize: 2,
        }}
        onFocus={(e) => (e.target.style.borderColor = T.accent)}
        onBlur={(e) => (e.target.style.borderColor = T.border)}
        onKeyDown={(e) => {
          // Tab 키 → 들여쓰기
          if (e.key === "Tab") {
            e.preventDefault();
            const { selectionStart: s, selectionEnd: end } = e.target;
            const next = value.slice(0, s) + "  " + value.slice(end);
            onChange(next);
            setTimeout(() => {
              e.target.selectionStart = e.target.selectionEnd = s + 2;
            }, 0);
          }
        }}
      />
      <div
        style={{
          textAlign: "right",
          fontSize: "11px",
          color: T.textMuted,
          marginTop: "4px",
        }}
      >
        {value.length.toLocaleString()}자
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// BlogWritePage
// ─────────────────────────────────────────────
const BlogWritePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, loading: authLoading } = useAuth();

  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [tags, setTags] = useState([]);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  const textareaRef = useRef();
  const isEditMode = !!id;

  // 관리자 체크
  useEffect(() => {
    if (!authLoading && !isAdmin) navigate("/blog", { replace: true });
  }, [isAdmin, authLoading, navigate]);

  // 수정 모드: getForEdit 사용 (조회수 증가 없음)
  useEffect(() => {
    if (!isEditMode) return;
    if (authLoading || !isAdmin) return;
    blogApi
      .getForEdit(id)
      .then(({ data }) => {
        setTitle(data.title);
        setContent(data.content);
        setTags(
          (data.tags || []).map((t) => (typeof t === "string" ? t : t.name))
        );
        setThumbnailUrl(data.thumbnail_url || null);
        const keys = Array.isArray(data.image_keys)
          ? data.image_keys
          : JSON.parse(data.image_keys || "[]");
        setUploadedImages(
          keys.map((k) => ({
            key: k,
            url: `${CDN}/blog/original/${k}.webp`,
            thumbnailUrl: `${CDN}/blog/thumb/${k}.webp`,
          }))
        );
      })
      .catch((err) => {
        console.error("Failed to fetch post for edit:", err);
        setFetchError("게시글을 불러오지 못했습니다.");
      });
  }, [id, isEditMode, authLoading, isAdmin]);

  // 이미지 업로드 완료
  const handleUpload = useCallback((imgData) => {
    setUploadedImages((prev) => [...prev, imgData]);
  }, []);

  // 이미지 삭제
  const handleDelete = useCallback(
    async (key) => {
      if (!window.confirm("이미지를 삭제하시겠습니까?")) return;
      try {
        await blogApi.deleteImage(key);
        setUploadedImages((prev) => {
          const next = prev.filter((i) => i.key !== key);
          const deleted = prev.find((i) => i.key === key);
          if (deleted?.thumbnailUrl === thumbnailUrl) setThumbnailUrl(null);
          return next;
        });
      } catch {
        alert("이미지 삭제에 실패했습니다.");
      }
    },
    [thumbnailUrl]
  );

  // 본문 커서 위치에 이미지 마크다운 삽입
  const handleInsert = useCallback(
    (url) => {
      const ta = textareaRef.current;
      if (!ta) return;
      const { selectionStart: s, selectionEnd: e } = ta;
      const md = `\n![이미지](${url})\n`;
      const next = content.slice(0, s) + md + content.slice(e);
      setContent(next);
      setTimeout(() => {
        ta.selectionStart = ta.selectionEnd = s + md.length;
        ta.focus();
      }, 0);
    },
    [content]
  );

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!title.trim() || !content.trim()) {
      alert("제목과 내용을 입력해주세요.");
      return;
    }
    setSubmitting(true);
    const payload = {
      title: title.trim(),
      content: content.trim(),
      tags,
      thumbnailUrl,
      imageKeys: uploadedImages.map((i) => i.key),
    };
    try {
      if (isEditMode) {
        await blogApi.update(id, payload);
        navigate(`/blog/${id}`);
      } else {
        const { data } = await blogApi.create(payload);
        navigate(`/blog/${data.id}`);
      }
    } catch (err) {
      alert(err.response?.data?.error || "저장에 실패했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading) return null;
  if (fetchError) {
    return (
      <div
        style={{ maxWidth: "800px", margin: "0 auto", padding: "48px 24px" }}
      >
        <p style={{ color: T.danger }}>{fetchError}</p>
      </div>
    );
  }

  const canSubmit = !submitting && title.trim() && content.trim();

  return (
    <div style={{ maxWidth: "800px", margin: "0 auto", padding: "48px 24px" }}>
      {/* 헤더 */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "36px",
        }}
      >
        <h1
          style={{
            fontSize: "22px",
            fontWeight: 600,
            color: T.text,
            margin: 0,
          }}
        >
          {isEditMode ? "글 수정" : "새 글 작성"}
        </h1>
        <button
          type="button"
          onClick={() => navigate("/blog")}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            color: T.textMuted,
            fontSize: "13px",
          }}
        >
          ← 목록으로
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        {/* 제목 */}
        <div style={{ marginBottom: "20px" }}>
          <input
            type="text"
            placeholder="제목"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            style={{
              width: "100%",
              padding: "14px 16px",
              border: `1px solid ${T.border}`,
              borderRadius: T.radius,
              fontSize: "20px",
              fontWeight: 600,
              background: T.bgSecond,
              color: T.text,
              boxSizing: "border-box",
              outline: "none",
            }}
            onFocus={(e) => (e.target.style.borderColor = T.accent)}
            onBlur={(e) => (e.target.style.borderColor = T.border)}
          />
          <div
            style={{
              textAlign: "right",
              fontSize: "11px",
              color: T.textMuted,
              marginTop: "4px",
            }}
          >
            {title.length} / 200
          </div>
        </div>

        {/* 태그 */}
        <TagInput tags={tags} onChange={setTags} />

        {/* 이미지 */}
        <ImagePanel
          uploadedImages={uploadedImages}
          onUpload={handleUpload}
          onDelete={handleDelete}
          onInsert={handleInsert}
        />

        {/* 대표 이미지 */}
        <ThumbnailPicker
          uploadedImages={uploadedImages}
          thumbnailUrl={thumbnailUrl}
          onSelect={setThumbnailUrl}
        />

        {/* 마크다운 에디터 */}
        <MarkdownEditor
          value={content}
          onChange={setContent}
          textareaRef={textareaRef}
        />

        {/* 버튼 */}
        <div
          style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}
        >
          <button
            type="button"
            onClick={() => navigate("/blog")}
            disabled={submitting}
            style={{
              padding: "11px 22px",
              border: `1px solid ${T.border}`,
              borderRadius: T.radius,
              background: "transparent",
              color: T.textMuted,
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            취소
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            style={{
              padding: "11px 26px",
              border: "none",
              borderRadius: T.radius,
              background: T.accent,
              color: "#fff",
              cursor: canSubmit ? "pointer" : "not-allowed",
              fontSize: "14px",
              fontWeight: 600,
              opacity: canSubmit ? 1 : 0.5,
              transition: "opacity 0.15s",
            }}
          >
            {submitting ? "저장 중…" : isEditMode ? "수정 완료" : "게시하기"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BlogWritePage;
